import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { PresaleDialogProvider } from "@/components/sections/PresaleDialog";

/**
 * Shared layout for /modules/<capability> pages. Owns:
 * - breadcrumb trail Home → Modules → <capability>
 * - document.title + meta description sync (prerender writes these to
 *   the static HTML; this just keeps SPA navigations consistent)
 */
export default function ModulePageLayout({
  title,
  description,
  breadcrumbLabel,
  heroEyebrow,
  heroHeading,
  heroBody,
  children,
}) {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", description);

    // Reveal on scroll (same pattern as /modules, /pricing, /assistants).
    const els = document.querySelectorAll(".zy-reveal");
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [title, description]);

  return (
    <PresaleDialogProvider>
      <div className="min-h-screen bg-white">
        <Navbar />

        <main className="pt-24">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="zy-container pt-6 pb-2 text-[13px] text-[#666]"
            data-testid="module-breadcrumb"
          >
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-[#1A4FFF]">Home</Link>
              </li>
              <li><ChevronRight size={14} className="opacity-50" /></li>
              <li>
                <Link to="/modules" className="hover:text-[#1A4FFF]">Modules</Link>
              </li>
              <li><ChevronRight size={14} className="opacity-50" /></li>
              <li className="text-slate-900 font-medium">{breadcrumbLabel}</li>
            </ol>
          </nav>

          {/* Hero */}
          <section className="zy-section pt-6" style={{ background: "#fff" }}>
            <div className="zy-container">
              <div className="max-w-3xl zy-reveal">
                <p className="zy-eyebrow mb-4" style={{ color: "#1A4FFF" }}>
                  {heroEyebrow}
                </p>
                <h1 className="zy-h1" data-testid="module-h1">{heroHeading}</h1>
                <p className="zy-body mt-5 text-[17px] leading-relaxed">
                  {heroBody}
                </p>
              </div>
            </div>
          </section>

          {children}

          {/* Footer CTA band */}
          <section className="zy-section" style={{ background: "#F6F8FB" }}>
            <div className="zy-container">
              <div className="max-w-3xl mx-auto text-center">
                <p className="zy-eyebrow mb-3" style={{ color: "#1A4FFF" }}>
                  One AI-native platform
                </p>
                <h2 className="zy-h2">
                  Part of the Zynthoro AI ERP ecosystem
                </h2>
                <p className="zy-body mt-5">
                  This capability is one of 12 connected modules in Zynthoro. Finance,
                  sales, HR, projects, marketing and operations share a single data
                  model and one AI assistant layer.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/modules"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-white"
                    style={{ background: "#1A4FFF" }}
                    data-testid="module-see-all"
                  >
                    See all 12 modules
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold border"
                    style={{ borderColor: "#1A4FFF", color: "#1A4FFF" }}
                    data-testid="module-see-pricing"
                  >
                    See pricing
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </PresaleDialogProvider>
  );
}
